import {
  WHATSAPP_COMMUNITY_LINK_REGEX,
  isValidWhatsAppCommunityLink,
  normalizeWhatsAppCommunityLink,
  validateOptionalWhatsAppCommunityLink,
} from '../../../utils/whatsappCommunityLink';

export {
  WHATSAPP_COMMUNITY_LINK_REGEX,
  isValidWhatsAppCommunityLink,
  normalizeWhatsAppCommunityLink,
  validateOptionalWhatsAppCommunityLink,
};

export const SHORT_DESCRIPTION_MAX = 120;

export const ABOUT_DESCRIPTION_MAX = 600;

export const EXTRACT_PARTNER_DETAILS_PROMPT_MAX = 20000;

export const EXTRACT_PARTNER_DETAILS_TIMEOUT_MS = 2 * 60 * 1000;

export const REGENERATE_PARTNER_AVATAR_FIELD = 'partnerAvatarImage';

export const REGENERATE_PARTNER_COVER_BANNER_FIELD = 'partnerCoverBannerImage';

export const PARTNER_MEDIA_ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

export const PARTNER_MEDIA_ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';

export const PARTNER_MEDIA_MAX_BYTES = 10 * 1024 * 1024;

export const PARTNER_MEDIA_SIZE_HINT = 'JPG, JPEG or PNG · Maximum 10 MB';

export const PARTNER_LOGO_OUTPUT_HINT = 'Optimized to 810×810 PNG for a circular crop';

export const PARTNER_COVER_OUTPUT_HINT = 'Optimized to 1536×1024 PNG';

export const PARTNER_MEDIA_OPTIMIZATION_TIMEOUT_MS = 2 * 60 * 1000;

const isPresent = (value) => value !== null && value !== undefined && value !== '';

/** Maps POST /admin/extract-partner-details `data` onto Company Info form fields. */
export const mapExtractedPartnerDetailsToFormPatch = (details) => {
  if (!details || typeof details !== 'object') return {};

  const patch = {};

  if (isPresent(details.shortDescription)) {
    patch.shortDescription = String(details.shortDescription)
      .trim()
      .slice(0, SHORT_DESCRIPTION_MAX);
  }

  if (isPresent(details.description)) {
    patch.description = String(details.description).trim().slice(0, ABOUT_DESCRIPTION_MAX);
  }

  return patch;
};

export const countFilledPartnerDescribeFields = (patch) =>
  Object.keys(patch).filter((key) => patch[key] !== '' && patch[key] != null).length;

export const extractPartnerCompanyRecord = (partnerUser) => {
  if (!partnerUser) return null;
  if (partnerUser.partnerProfile && typeof partnerUser.partnerProfile === 'object') {
    return partnerUser.partnerProfile;
  }
  return partnerUser;
};

export const getCompanyInfoMediaBaseline = (partnerUser) => {
  const record = extractPartnerCompanyRecord(partnerUser);
  return {
    avatarUrl: record?.avatarUrl ?? record?.logoUrl ?? partnerUser?.avatarUrl ?? null,
    coverBannerUrl: record?.coverBannerUrl ?? null,
  };
};

export const createCompanyInfoInitialState = (partnerUser) => {
  const record = extractPartnerCompanyRecord(partnerUser);
  const social = record?.socialLinks ?? partnerUser?.socialLinks ?? {};

  return {
    fullName:
      record?.fullName ??
      record?.displayName ??
      partnerUser?.fullName ??
      partnerUser?.displayName ??
      '',
    shortDescription: record?.shortDescription ?? partnerUser?.shortDescription ?? '',
    description:
      record?.description ??
      record?.aboutDescription ??
      partnerUser?.description ??
      partnerUser?.aboutDescription ??
      '',
    logoPreview: record?.avatarUrl ?? record?.logoUrl ?? partnerUser?.avatarUrl ?? '',
    logoFile: null,
    logoRemoved: false,
    coverPreview: record?.coverBannerUrl ?? '',
    coverFile: null,
    coverRemoved: false,
    websiteUrl: social.website ?? '',
    instagramUrl: social.instagram ?? '',
    facebookUrl: social.facebook ?? '',
    whatsappUrl: normalizeWhatsAppCommunityLink(
      record?.whatsappCommunityLink ?? partnerUser?.whatsappCommunityLink ?? '',
    ),
  };
};

export const validateCompanyInfo = (form, partnerProfile, { communityCreated = false } = {}) => {
  const errors = {};
  const baseline = getCompanyInfoMediaBaseline(partnerProfile);

  if (!form.fullName?.trim()) {
    errors.fullName = 'Display name is required';
  }

  if (!form.shortDescription?.trim()) {
    errors.shortDescription = 'Short description is required';
  }

  if (!form.description?.trim()) {
    errors.description = 'About description is required';
  }

  const hasLogo = Boolean(
    form.logoFile || (!form.logoRemoved && (form.logoPreview || baseline.avatarUrl)),
  );
  if (!hasLogo) {
    errors.logo = 'Logo is required';
  } else if (form.logoFile instanceof File) {
    const logoFileError = validatePartnerMediaFile(form.logoFile, 'Logo');
    if (!logoFileError.valid) errors.logo = logoFileError.message;
  }

  const hasCover = Boolean(
    form.coverFile || (!form.coverRemoved && (form.coverPreview || baseline.coverBannerUrl)),
  );
  if (!hasCover) {
    errors.cover = 'Cover banner is required';
  } else if (form.coverFile instanceof File) {
    const coverFileError = validatePartnerMediaFile(form.coverFile, 'Cover banner');
    if (!coverFileError.valid) errors.cover = coverFileError.message;
  }

  const websiteError = validateOptionalHttpUrl(form.websiteUrl, 'website');
  if (websiteError) errors.websiteUrl = websiteError;

  const instagramError = validateOptionalSocialUrl(form.instagramUrl, {
    label: 'Instagram',
    hosts: ['instagram.com'],
  });
  if (instagramError) errors.instagramUrl = instagramError;

  const facebookError = validateOptionalSocialUrl(form.facebookUrl, {
    label: 'Facebook',
    hosts: ['facebook.com', 'fb.com'],
  });
  if (facebookError) errors.facebookUrl = facebookError;

  if (communityCreated) {
    const whatsappError = validateOptionalWhatsAppCommunityLink(form.whatsappUrl);
    if (whatsappError) errors.whatsappUrl = whatsappError;
  }

  return errors;
};

const COMPANY_INFO_FIELD_LABELS = {
  fullName: 'display name',
  shortDescription: 'short description',
  description: 'about description',
  logo: 'logo',
  cover: 'cover banner',
  websiteUrl: 'website link',
  instagramUrl: 'Instagram link',
  facebookUrl: 'Facebook link',
  whatsappUrl: 'WhatsApp Community link',
};

const formatReadableList = (items) => {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export const getCompanyInfoSaveErrorMessage = (errors) => {
  const keys = Object.keys(errors || {});
  if (keys.length === 0) return '';
  if (keys.length === 1) {
    return errors[keys[0]];
  }

  const labels = keys.map((key) => COMPANY_INFO_FIELD_LABELS[key] || key);
  return `Please check the ${formatReadableList(labels)} below, then try saving again.`;
};

const hasFilledText = (value) => Boolean(String(value ?? '').trim());

export const isValidHttpUrl = (value) => {
  try {
    const url = new URL(String(value).trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const hostnameMatches = (value, hosts) => {
  try {
    const hostname = new URL(String(value).trim()).hostname.replace(/^www\./i, '').toLowerCase();
    return hosts.some((host) => hostname === host || hostname.endsWith(`.${host}`));
  } catch {
    return false;
  }
};

export const validateOptionalHttpUrl = (value, label = 'website') => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  if (!isValidHttpUrl(trimmed)) {
    return `Enter a valid ${label} URL starting with http:// or https://`;
  }
  return '';
};

export const validateOptionalSocialUrl = (value, { label, hosts }) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  if (!isValidHttpUrl(trimmed)) {
    return `Enter a valid ${label} URL starting with http:// or https://`;
  }
  if (hosts?.length && !hostnameMatches(trimmed, hosts)) {
    return `Enter a valid ${label} URL`;
  }
  return '';
};

/** Prompt hides only when the saved API profile already has both short and about copy. */
export const shouldShowCompanyDescribePrompt = (partnerUser) => {
  if (!partnerUser) return false;

  const record = extractPartnerCompanyRecord(partnerUser) ?? partnerUser;
  const shortDescription = record.shortDescription ?? partnerUser.shortDescription;
  const description =
    record.description ??
    record.aboutDescription ??
    partnerUser.description ??
    partnerUser.aboutDescription;

  return !(hasFilledText(shortDescription) && hasFilledText(description));
};

const isAllowedPartnerMediaType = (file) => {
  if (!(file instanceof File)) return false;
  if (PARTNER_MEDIA_ALLOWED_TYPES.has(file.type)) return true;
  return /\.(jpe?g|png)$/i.test(file.name);
};

export const validatePartnerMediaFile = (file, noun = 'Image') => {
  if (!(file instanceof File)) {
    return { valid: false, message: `Please select a ${noun.toLowerCase()} to upload.` };
  }

  if (!isAllowedPartnerMediaType(file)) {
    return { valid: false, message: `${noun} must be JPG, JPEG, or PNG.` };
  }

  if (file.size > PARTNER_MEDIA_MAX_BYTES) {
    return { valid: false, message: `${noun} must be 10 MB or smaller.` };
  }

  return { valid: true };
};

const getSlotSubmitBlockMessage = (
  { file, preview },
  { optimizing = false, pendingUpload = false, optimizationError = '', noun },
) => {
  if (optimizing) {
    return `Please wait for ${noun} optimization to finish.`;
  }

  if (pendingUpload || optimizationError) {
    return `${noun} optimization must complete before saving.`;
  }

  const previewUrl = String(preview ?? '');
  if (previewUrl.startsWith('blob:') && !(file instanceof File)) {
    return `${noun} optimization must complete before saving.`;
  }

  return null;
};

export const getCompanyMediaSubmitBlockMessage = (form, logoState = {}, coverState = {}) =>
  getSlotSubmitBlockMessage(
    { file: form?.logoFile, preview: form?.logoPreview },
    { ...logoState, noun: 'logo' },
  ) ||
  getSlotSubmitBlockMessage(
    { file: form?.coverFile, preview: form?.coverPreview },
    { ...coverState, noun: 'cover banner' },
  );
